import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Button, PhotoField, SignatureCapture } from '../../components/ui';
import { EyeIcon, PencilIcon, TrashIcon } from '../../components/ui/Icons';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../components/ui/Modal';
import { SignatureDisplay, type SignatureAsset, type SignatureValue } from '../../components/signature';
import {
  crewSignatureDownloadPath,
  crewSignatureValue,
  deleteCrewPhoto,
  deleteCrewSignature,
  uploadCrewPhoto,
  uploadCrewSignatureAsset,
  useCrewFile,
  useCrewPhotoBlobUrl,
} from './useData';

type Props = {
  crewId: number;
  readOnly?: boolean;
};

export type CrewMediaCommitHandle = {
  /** Persist pending photo/signature changes to the server. */
  commit: () => Promise<void>;
  hasPending: () => boolean;
};

type PhotoPending =
  | null
  | { type: 'file'; file: File; url: string }
  | { type: 'remove' };

type SignaturePending =
  | null
  | { type: 'file'; file: File; url: string; source?: 'draw' | 'upload' }
  | { type: 'remove' };

function crewInitials(crew: {
  firstName?: string | null;
  lastName?: string | null;
} | null | undefined): string {
  const first = (crew?.firstName ?? '').trim().charAt(0);
  const last = (crew?.lastName ?? '').trim().charAt(0);
  const letters = `${first}${last}`.toUpperCase();
  return letters || '?';
}

function revokeUrl(url: string | undefined) {
  if (url?.startsWith('blob:')) {
    try {
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  }
}

/**
 * Compact photo + signature strip for the Personal tab.
 * When editable, changes stay local until {@link CrewMediaCommitHandle.commit}
 * (called from the crew form Save).
 */
export const CrewPhotoSignaturePanel = React.forwardRef<CrewMediaCommitHandle, Props>(
  function CrewPhotoSignaturePanel({ crewId, readOnly = false }, ref) {
    const { colors, theme } = useTheme();
    const isDark = theme === 'dark';
    const { data: crew, mutate } = useCrewFile(crewId);
    const { photoBlobUrl, loading: photoLoading } = useCrewPhotoBlobUrl(
      crewId,
      crew?.photoStorageKey,
    );
    const [photoBusy, setPhotoBusy] = React.useState(false);
    const [photoError, setPhotoError] = React.useState<string | null>(null);
    const [sigModal, setSigModal] = React.useState<'edit' | 'view' | null>(null);
    const [photoPending, setPhotoPending] = React.useState<PhotoPending>(null);
    const [signaturePending, setSignaturePending] = React.useState<SignaturePending>(null);
    const photoPendingRef = React.useRef(photoPending);
    const signaturePendingRef = React.useRef(signaturePending);
    photoPendingRef.current = photoPending;
    signaturePendingRef.current = signaturePending;

    const defer = !readOnly;

    React.useEffect(() => {
      return () => {
        const p = photoPendingRef.current;
        const s = signaturePendingRef.current;
        if (p?.type === 'file') revokeUrl(p.url);
        if (s?.type === 'file') revokeUrl(s.url);
      };
    }, []);

    const serverSignature = crewSignatureValue(crew);

    const displayPhotoUrl =
      photoPending?.type === 'remove'
        ? null
        : photoPending?.type === 'file'
          ? photoPending.url
          : photoBlobUrl;

    const hasPhoto =
      photoPending?.type === 'remove'
        ? false
        : photoPending?.type === 'file'
          ? true
          : Boolean(crew?.photoStorageKey);

    const displaySignature: SignatureValue | null =
      signaturePending?.type === 'remove'
        ? null
        : signaturePending?.type === 'file'
          ? {
              storageKey: signaturePending.url,
              fileName: signaturePending.file.name,
              mimeType: signaturePending.file.type || 'image/png',
              size: signaturePending.file.size,
              source: signaturePending.source,
              subjectType: 'crew',
              subjectId: crewId,
            }
          : serverSignature;

    const hasSignature = Boolean(displaySignature?.storageKey);
    const mediaDirty = Boolean(photoPending || signaturePending);

    const downloadPath = React.useCallback(
      (key: string) => {
        if (key.startsWith('blob:') || key.startsWith('data:')) return key;
        return crewSignatureDownloadPath(crewId, key);
      },
      [crewId],
    );

    React.useImperativeHandle(
      ref,
      () => ({
        hasPending: () => Boolean(photoPendingRef.current || signaturePendingRef.current),
        commit: async () => {
          if (!defer) return;
          const photo = photoPendingRef.current;
          const signature = signaturePendingRef.current;
          if (!photo && !signature) return;

          if (photo?.type === 'remove') {
            await deleteCrewPhoto(crewId);
          } else if (photo?.type === 'file') {
            await uploadCrewPhoto(crewId, photo.file);
          }

          if (signature?.type === 'remove') {
            await deleteCrewSignature(crewId);
          } else if (signature?.type === 'file') {
            await uploadCrewSignatureAsset(crewId, signature.file, signature.source);
          }

          if (photo?.type === 'file') revokeUrl(photo.url);
          if (signature?.type === 'file') revokeUrl(signature.url);
          setPhotoPending(null);
          setSignaturePending(null);
          await mutate();
        },
      }),
      [crewId, defer, mutate],
    );

    const onPhotoPick = async (file: File) => {
      if (defer) {
        setPhotoPending((prev) => {
          if (prev?.type === 'file') revokeUrl(prev.url);
          return { type: 'file', file, url: URL.createObjectURL(file) };
        });
        setPhotoError(null);
        return;
      }
      setPhotoBusy(true);
      setPhotoError(null);
      try {
        await uploadCrewPhoto(crewId, file);
        await mutate();
      } catch (e: unknown) {
        const msg =
          (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Photo upload failed';
        setPhotoError(Array.isArray(msg) ? msg.join(', ') : String(msg));
      } finally {
        setPhotoBusy(false);
      }
    };

    const onPhotoRemove = async () => {
      if (defer) {
        setPhotoPending((prev) => {
          if (prev?.type === 'file') revokeUrl(prev.url);
          return { type: 'remove' };
        });
        setPhotoError(null);
        return;
      }
      setPhotoBusy(true);
      setPhotoError(null);
      try {
        await deleteCrewPhoto(crewId);
        await mutate();
      } catch {
        setPhotoError('Could not remove photo');
      } finally {
        setPhotoBusy(false);
      }
    };

    const onSignatureUpload = async (
      file: File,
      meta?: { source?: 'draw' | 'upload' },
    ): Promise<SignatureAsset> => {
      if (!defer) {
        return uploadCrewSignatureAsset(crewId, file, meta?.source);
      }
      const url = URL.createObjectURL(file);
      setSignaturePending((prev) => {
        if (prev?.type === 'file') revokeUrl(prev.url);
        return { type: 'file', file, url, source: meta?.source };
      });
      return {
        storageKey: url,
        fileName: file.name,
        mimeType: file.type || 'image/png',
        size: file.size,
      };
    };

    const onSignatureChange = async (next: SignatureValue | null) => {
      if (defer) {
        if (!next) {
          setSignaturePending((prev) => {
            if (prev?.type === 'file') revokeUrl(prev.url);
            return { type: 'remove' };
          });
        }
        return;
      }
      if (!next) {
        await deleteCrewSignature(crewId);
        await mutate();
        return;
      }
      await mutate();
    };

    if (!crewId) {
      return (
        <p className={cn('text-xs', colors.textMuted)}>
          Save the crew file first to upload a photo and signature.
        </p>
      );
    }

    const statusPill = (
      <span
        className={cn(
          'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide',
          mediaDirty
            ? isDark
              ? 'bg-amber-500/20 text-amber-300'
              : 'bg-amber-100 text-amber-900'
            : hasSignature
              ? isDark
                ? 'bg-emerald-500/20 text-emerald-300'
                : 'bg-emerald-100 text-emerald-800'
              : isDark
                ? 'bg-white/10 text-slate-400'
                : 'bg-slate-200/80 text-slate-600',
        )}
      >
        {mediaDirty ? 'Unsaved' : hasSignature ? 'On file' : 'Missing'}
      </span>
    );

    return (
      <>
        <div
          className={cn(
            'flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border px-3 py-2',
            colors.border,
            isDark ? 'bg-white/[0.03]' : 'bg-slate-50/90',
          )}
        >
          <div className="flex items-center gap-2">
            <span className={cn('hidden text-[11px] font-medium sm:inline', colors.textMuted)}>
              Photo
            </span>
            <PhotoField
              variant="avatar"
              size="sm"
              actionsPlacement="side"
              imageUrl={displayPhotoUrl}
              loading={!photoPending && photoLoading}
              filename={
                photoPending?.type === 'file'
                  ? photoPending.file.name
                  : crew?.photoOriginalFilename
              }
              hasImage={hasPhoto}
              initials={crewInitials(crew)}
              emptyLabel="—"
              editable={!readOnly}
              busy={photoBusy}
              error={photoError}
              onPick={onPhotoPick}
              onRemove={readOnly ? undefined : onPhotoRemove}
            />
          </div>

          <div
            className={cn(
              'hidden h-8 w-px shrink-0 sm:block',
              isDark ? 'bg-white/10' : 'bg-slate-200',
            )}
            aria-hidden
          />

          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <span className={cn('hidden text-[11px] font-medium sm:inline', colors.textMuted)}>
              Signature
            </span>
            <button
              type="button"
              disabled={!hasSignature}
              onClick={() => hasSignature && setSigModal('view')}
              className={cn(
                'flex h-10 w-[160px] shrink-0 items-center justify-center overflow-hidden rounded-md border bg-white px-2 dark:bg-slate-950 sm:w-[200px]',
                colors.border,
                hasSignature && 'cursor-pointer hover:ring-2 hover:ring-sky-400/40',
                !hasSignature && 'border-dashed',
              )}
              title={hasSignature ? 'View signature' : undefined}
            >
              {hasSignature ? (
                <SignatureDisplay
                  value={displaySignature}
                  downloadPath={downloadPath}
                  height={32}
                  showStamp={false}
                  className="!rounded-none !border-0 w-full"
                  emptyLabel="…"
                />
              ) : (
                <span className={cn('text-[11px]', colors.textMuted)}>No signature</span>
              )}
            </button>

            {statusPill}

            <div className="flex items-center gap-1">
              {hasSignature ? (
                <Button
                  type="button"
                  size="icon"
                  variant="secondary"
                  className="h-8 w-8"
                  title="View signature"
                  aria-label="View signature"
                  onClick={() => setSigModal('view')}
                >
                  <EyeIcon className="h-3.5 w-3.5" />
                </Button>
              ) : null}
              {!readOnly ? (
                <>
                  <Button
                    type="button"
                    size="icon"
                    variant="secondary"
                    className="h-8 w-8"
                    title={hasSignature ? 'Replace signature' : 'Add signature'}
                    aria-label={hasSignature ? 'Replace signature' : 'Add signature'}
                    onClick={() => setSigModal('edit')}
                  >
                    <PencilIcon className="h-3.5 w-3.5" />
                  </Button>
                  {hasSignature ? (
                    <Button
                      type="button"
                      size="icon"
                      variant="danger"
                      className="h-8 w-8"
                      title="Remove signature"
                      aria-label="Remove signature"
                      onClick={() => void onSignatureChange(null)}
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                    </Button>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>
        </div>

        {defer && mediaDirty ? (
          <p className={cn('mt-1.5 text-[11px]', colors.textMuted)}>
            Photo/signature changes apply when you save the crew file.
          </p>
        ) : null}

        <Modal isOpen={sigModal === 'view'} onClose={() => setSigModal(null)} size="lg">
          <ModalHeader>
            <ModalTitle>Signature</ModalTitle>
          </ModalHeader>
          <ModalContent>
            <SignatureDisplay
              value={displaySignature}
              downloadPath={downloadPath}
              height={160}
              emptyLabel="No signature"
            />
          </ModalContent>
          <ModalFooter>
            {!readOnly ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setSigModal('edit')}
              >
                Edit
              </Button>
            ) : null}
            <Button type="button" variant="secondary" size="sm" onClick={() => setSigModal(null)}>
              Close
            </Button>
          </ModalFooter>
        </Modal>

        <Modal isOpen={sigModal === 'edit'} onClose={() => setSigModal(null)} size="lg">
          <ModalHeader>
            <ModalTitle>{hasSignature ? 'Replace signature' : 'Add signature'}</ModalTitle>
          </ModalHeader>
          <ModalContent>
            <p className={cn('mb-3 text-xs', colors.textMuted)}>
              {defer
                ? 'Draw or upload here. The signature is stored when you save the crew file.'
                : 'Draw on the pad or upload an image.'}
            </p>
            <SignatureCapture
              title=""
              value={displaySignature}
              onChange={(next) => void onSignatureChange(next)}
              upload={onSignatureUpload}
              downloadPath={downloadPath}
              allowStamp={false}
              height={200}
            />
          </ModalContent>
          <ModalFooter>
            <Button type="button" variant="secondary" size="sm" onClick={() => setSigModal(null)}>
              Done
            </Button>
          </ModalFooter>
        </Modal>
      </>
    );
  },
);

export default CrewPhotoSignaturePanel;
